/**
 * Stable test ids for the authentication surface (login / signup forms).
 *
 * Single source of truth shared by the React source — which renders these as
 * `data-testid` — and the Playwright suite, which queries them via
 * `getByTestId`. Because both sides import the same enum, renaming an id is a
 * compile error in the tests rather than a silent runtime mismatch.
 *
 * Ids are keyed by element ROLE, not by which form renders them: the login and
 * signup panels are mutually exclusive in the DOM (see login-page-content's
 * `switch (type)`), so the same `AUTH_EMAIL_INPUT` correctly resolves to
 * "the auth email field currently on screen" in either flow.
 *
 * Convention: the enum name is the feature, each member is the element, and the
 * value is SCREAMING_SNAKE_CASE carrying the feature prefix so the id is
 * self-describing in the DOM and in test reports.
 */
export enum AuthTestId {
  EMAIL_INPUT = 'AUTH_EMAIL_INPUT',
  PASSWORD_INPUT = 'AUTH_PASSWORD_INPUT',
  CONFIRM_PASSWORD_INPUT = 'AUTH_CONFIRM_PASSWORD_INPUT',
  SUBMIT_BUTTON = 'AUTH_SUBMIT_BUTTON',
  RESET_PASSWORD_BUTTON = 'AUTH_RESET_PASSWORD_BUTTON',
  // The "Continue with email" CTA that reveals the email form (shared by login + signup panels).
  SHOW_FORM_BUTTON = 'AUTH_SHOW_FORM_BUTTON',
  FORGOT_PASSWORD_LINK = 'AUTH_FORGOT_PASSWORD_LINK',
  // Inline per-field validation errors shown under the email / password inputs.
  EMAIL_ERROR = 'AUTH_EMAIL_ERROR',
  PASSWORD_ERROR = 'AUTH_PASSWORD_ERROR',
  // Signup password-policy criteria rows.
  PASSWORD_POLICY_LENGTH = 'AUTH_PASSWORD_POLICY_LENGTH',
  PASSWORD_POLICY_CHARACTERS = 'AUTH_PASSWORD_POLICY_CHARACTERS',
  PASSWORD_POLICY_MISMATCH = 'AUTH_PASSWORD_POLICY_MISMATCH',
  NOTIFY_PROMOTIONS_CHECKBOX = 'AUTH_NOTIFY_PROMOTIONS_CHECKBOX',
  PASSWORD_TOGGLE = 'AUTH_PASSWORD_TOGGLE',
  // Top-of-form notification/error banner (login error, etc.).
  ERROR_BANNER = 'AUTH_ERROR_BANNER',
  // The auth panel heading — one id per panel so a test asserts the right panel is shown.
  HEADING_LOGIN = 'AUTH_HEADING_LOGIN',
  HEADING_SIGNUP = 'AUTH_HEADING_SIGNUP',
  HEADING_STUDENT_LOGIN = 'AUTH_HEADING_STUDENT_LOGIN',
  HEADING_CONFIRM_LOGIN = 'AUTH_HEADING_CONFIRM_LOGIN',
  HEADING_FORGOT_PASSWORD = 'AUTH_HEADING_FORGOT_PASSWORD',
  STUDENT_PROJECT_INPUT = 'AUTH_STUDENT_PROJECT_INPUT',
  // Footer CTA row prompt ("Already have an account?") + the CTA buttons.
  FOOTER_LOGIN_PROMPT = 'AUTH_FOOTER_LOGIN_PROMPT',
  SIGN_UP_FOR_FREE_BUTTON = 'AUTH_SIGN_UP_FOR_FREE_BUTTON',
  USE_STUDENT_LOGIN_BUTTON = 'AUTH_USE_STUDENT_LOGIN_BUTTON',
  // Footer "visit our page" social links.
  SOCIAL_FACEBOOK = 'AUTH_SOCIAL_FACEBOOK',
  SOCIAL_INSTAGRAM = 'AUTH_SOCIAL_INSTAGRAM',
  SOCIAL_X = 'AUTH_SOCIAL_X',
  SOCIAL_YOUTUBE = 'AUTH_SOCIAL_YOUTUBE',
  // Post-login elements (PHP-rendered, gated via pmwTestIdAttr) that the auth page object
  // uses as success signals: the primary dashboard nav and the My Stuff loader.
  DASHBOARD_NAV = 'AUTH_DASHBOARD_NAV',
  MYSTUFF_LOADER = 'AUTH_MYSTUFF_LOADER',
}
